/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState, useId } from 'react';
import Icon from "../Icon";
import styled from "styled-components";
import Text from "../Text";
import { useEscapeKey } from "../../../hooks/a11y/useEscapeKey";
import { isActivationKey } from "../../../hooks/a11y/activationKeys";
import { useClickOutside } from "../../../hooks/useClickOutside";

const SelectFieldWrapper = styled.div`
  position: relative;
  width: 100%;
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.375rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
`;

const SelectTrigger = styled.div`
  padding: 7px 16px;
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  border-radius: 8px;
  color: rgba(${({ theme }) => theme.colors.text.rgba});
  background: transparent;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  width: 60%;
  height: 100%;
`;

const SelectOptions = styled.ul`
  margin: 0;
  padding: 4px 0;
  list-style: none;
  position: absolute;
  top: calc(100% + 1px);
  left: 0;
  right: 0;
  background: #fff;
  z-index: 10;
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
`;

const Option = styled.li`
  padding: 8px 16px;
  cursor: pointer;
  transition: background 0.2s;
  color: rgba(${({ theme }) => theme.colors.text.rgba});
  @media (hover: hover) {
      &:hover {
          background: rgba(${({ theme }) => theme.colors.accent.rgb}, 0.1);
      }
  }
  &.active {
    font-weight: bold;
  }
`;

const SelectField = ({ value, onChange, options, label, placeholder = "", disabled = false }) => {
  const [open, setOpen] = useState(false);
  const listboxId = useId();
  const selectRef = useClickOutside(() => setOpen(false));
  const selectedOption = options.find((opt) => opt.value === value);

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
  };

  useEscapeKey(() => setOpen(false), open);

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (isActivationKey(e)) {
      e.preventDefault();
      setOpen((prev) => !prev);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
    }
  };

  const handleOptionKeyDown = (e, val) => {
    if (isActivationKey(e)) {
      e.preventDefault();
      handleSelect(val);
    }
  };

  return (
    <SelectFieldWrapper ref={selectRef}>
      {label && <Text children={label} color="dark"/>}
      <SelectTrigger
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={label || undefined}
        aria-disabled={disabled || undefined}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        onKeyDown={handleTriggerKeyDown}
      >
        {selectedOption?.label} &nbsp;
        {placeholder && <Text children={placeholder} /> }
        {open ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
      </SelectTrigger>

      {open && (
        <SelectOptions role="listbox" id={listboxId}>
          {options.map((opt) => (
            <Option key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              tabIndex={0}
              className={opt.value === value ? "active" : ""}
              onClick={() => handleSelect(opt.value)}
              onKeyDown={(e) => handleOptionKeyDown(e, opt.value)} >
                {opt.label}
            </Option>
          ))}
        </SelectOptions>
      )}
    </SelectFieldWrapper>
  );
};

export default SelectField;